import { useRef, useEffect, useState, type PointerEvent } from 'react';
import RailwayImg from '@/shared/assets/optimized/railway-1600.webp';
import RailwaySmall from '@/shared/assets/optimized/railway-640.webp';
import RailwayMedium from '@/shared/assets/optimized/railway-960.webp';
import './PhotoReveal.css';

export function PhotoReveal() {
    const [visible, setVisible] = useState(false);
    const bounds = useRef<DOMRect | null>(null);
    const frameRef = useRef<HTMLDivElement>(null);
    const pending = useRef(0);
    const pointer = useRef({ x: 0, y: 0 });
    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) { setVisible(true); observer.disconnect(); }
        }, { rootMargin: '250px' });
        if (frameRef.current) observer.observe(frameRef.current);
        return () => observer.disconnect();
    }, []);
    useEffect(() => {
        const invalidate = () => { bounds.current = null; };
        window.addEventListener('scroll', invalidate, { passive: true, capture: true });
        window.addEventListener('resize', invalidate);
        return () => {
            cancelAnimationFrame(pending.current);
            window.removeEventListener('scroll', invalidate, true);
            window.removeEventListener('resize', invalidate);
        };
    }, []);
    function reveal(event: PointerEvent<HTMLDivElement>) {
        if (event.pointerType !== 'mouse') return;
        pointer.current = { x: event.clientX, y: event.clientY };
        if (pending.current) return;
        pending.current = requestAnimationFrame(() => {
            pending.current = 0;
            const frame = frameRef.current;
            if (!frame) return;
            // Read once on entry/scroll/resize, then batch writes once per frame.
            bounds.current ??= frame.getBoundingClientRect();
            frame.style.setProperty('--reveal-x', `${pointer.current.x - bounds.current.left}px`);
            frame.style.setProperty('--reveal-y', `${pointer.current.y - bounds.current.top}px`);
            frame.dataset.revealing = 'true';
        });
    }

    return (
        <figure className="project-photo photo-reveal">
            <div
                ref={frameRef}
                className="photo-reveal__frame"
                tabIndex={0}
                aria-label="Фотография железнодорожного макета"
                onPointerEnter={reveal}
                onPointerMove={reveal}
                onPointerLeave={(event) => {
                    cancelAnimationFrame(pending.current);
                    pending.current = 0;
                    bounds.current = null;
                    delete event.currentTarget.dataset.revealing;
                }}
            >
                <div className="photo-reveal__drawing" aria-hidden="true">
                    <span className="photo-reveal__index">03 / По ту сторону экрана</span>
                    <span className="photo-reveal__title">
                        Управляй
                        <br />
                        Ораганизуй
                        <br />
                        Следи
                    </span>
                    <span className="photo-reveal__cross">+</span>
                </div>
                <img
                    className="photo-reveal__image"
                    src={visible ? RailwayImg : undefined}
                    srcSet={visible ? `${RailwaySmall} 640w, ${RailwayMedium} 960w, ${RailwayImg} 1600w` : undefined}
                    sizes="(max-width: 760px) calc(100vw - 40px), (max-width: 1440px) calc(100vw - 96px), 1344px"
                    fetchPriority="low"
                    alt="Железнодорожный макет с локомотивами и станциями"
                    width="1200"
                    height="539"
                    loading="lazy"
                    decoding="async"
                />
            </div>
        </figure>
    );
}
